import { getProductos, createProducto, updateProducto, deleteProducto } from '../models/productoModel.js';
import Swal from 'sweetalert2';
import pb from '../lib/pocketbase';
import { toasts } from "svelte-toasts";

export async function fetchProductos(page, perPage) {
  try {
    const response = await getProductos(page, perPage);
    
    // Agregar primera imagen de galería y categorías a cada producto
    const productosWithImages = await Promise.all(
      response.items.map(async (producto) => {
        try {
          const [firstImage, categorias] = await Promise.all([
            fetchFirstGalleryImage(producto.id),
            fetchCategoriasProducto(producto.id)
          ]);
          
          // Format parent products for table display (convert expanded objects to display format)
          let formattedParent = [];
          if (producto.expand && producto.expand.parent) {
            const parentArray = Array.isArray(producto.expand.parent) 
              ? producto.expand.parent 
              : [producto.expand.parent];
            formattedParent = parentArray.map(p => ({
              id: p.id,
              nombre: p.nombre,
              descrip: p.descrip
            }));
          }
          
          return { ...producto, firstImage, categorias, parent: formattedParent };
        } catch (error) {
          console.warn(`Error fetching data for producto ${producto.id}:`, error);
          return { ...producto, firstImage: null, categorias: [], parent: [] };
        }
      })
    );
    
    return {
      producto: productosWithImages,
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    console.error("Error fetching productos:", error);
    Swal.fire('Error', 'No se pudieron cargar los productos', 'error');
    return null;
  }
}

export async function saveProducto(producto, currentProductoId) {
  try {
    let productoId;

    // Transform parent multiselect format to array of IDs
    const productoData = { ...producto };
    if (productoData.parent && Array.isArray(productoData.parent)) {
      // Check if parent is in multiselect format [{key, value}]
      if (productoData.parent.length > 0 && productoData.parent[0]?.key) {
        productoData.parent = productoData.parent.map(p => p.key);
      }
    }

    // Transform categorias multiselect format (handled separately, so remove from main data)
    delete productoData.categorias;
    delete productoData.atributos;
    delete productoData.firstImage; // Remove computed fields

    if (currentProductoId) {
      await updateProducto(currentProductoId, productoData);
      productoId = currentProductoId;
      toasts.add({
        title: 'Éxito',
        description: 'Producto guardado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true,
      });
    } else {
      const newProducto = await createProducto(productoData);
      productoId = newProducto.id;
      toasts.add({
        title: 'Éxito',
        description: 'Producto creado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true,
      });
    }

    // Llamar a saveAtributosProducto y saveCategoriasProducto después de guardar el producto
    if (producto['atributos'] && producto['atributos'].length > 0) {
      await saveAtributosProducto(productoId, producto['atributos']);
    }
    
    if (producto['categorias'] && producto['categorias'].length > 0) {
      await saveCategoriasProducto(productoId, producto['categorias']);
    }

    return true;
  } catch (error) {
    console.error("Error saving producto:", error);
    toasts.add({
      title: 'Error',
      description: 'Ocurrió un error al guardar el producto',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
    return false;
  }
}

export async function removeProducto(id) {
  try {
    await deleteProducto(id);
    toasts.add({
      title: 'Éxito',
      description: 'Producto eliminado exitosamente.',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
  } catch (error) {
    console.error("Error deleting producto:", error);
    Swal.fire('Error', 'No se pudo eliminar el producto', 'error');
  }
}

// Suscripción en tiempo real a cambios en la colección 'producto'
export function subscribeToProductos(searchQuery, handleSearch) {
  pb.collection('producto').subscribe('*', async (e) => {
    console.log('Evento en tiempo real recibido:', e);
    await handleSearch(); // Actualiza la lista manteniendo la página actual
  });
}

// Desuscripción de los eventos en tiempo real
export function unsubscribeFromProductos() {
  pb.collection('producto').unsubscribe('*');
}

export async function searchProductos(query, page = 1, perPage = 10) {
  try {
    const response = await pb.collection('producto').getList(page, perPage, {
      filter: `descrip ~ "${query}" || nombre ~ "${query}"`,
      expand: 'parent'
    });
    
    // Agregar primera imagen de galería y categorías a cada producto
    const productosWithImages = await Promise.all(
      response.items.map(async (producto) => {
        try {
          const [firstImage, categorias] = await Promise.all([
            fetchFirstGalleryImage(producto.id),
            fetchCategoriasProducto(producto.id)
          ]);
          
          // Format parent products for table display (convert expanded objects to display format)
          let formattedParent = [];
          if (producto.expand && producto.expand.parent) {
            const parentArray = Array.isArray(producto.expand.parent) 
              ? producto.expand.parent 
              : [producto.expand.parent];
            formattedParent = parentArray.map(p => ({
              id: p.id,
              nombre: p.nombre,
              descrip: p.descrip
            }));
          }
          
          return { ...producto, firstImage, categorias, parent: formattedParent };
        } catch (error) {
          console.warn(`Error fetching data for producto ${producto.id}:`, error);
          return { ...producto, firstImage: null, categorias: [], parent: [] };
        }
      })
    );
    
    return {
      producto: productosWithImages,
      totalRecords: response.totalItems,
      currentPage: response.currentPage,
    };
  } catch (error) {
    console.error("Error searching productos:", error);
    Swal.fire('Error', 'No se pudieron buscar los productos', 'error');
    return null;
  }
}

// Fetch first gallery image for a producto
export async function fetchFirstGalleryImage(productoId) {
  try {
    const response = await pb.collection('producto_gallery').getList(1, 1, {
      filter: `producto="${productoId}"`,
      sort: '-created',
      $autoCancel: false
    });
    
    if (response.items && response.items.length > 0) {
      const galleryItem = response.items[0];
      return {
        id: galleryItem.id,
        url: galleryItem.url,
        collectionId: galleryItem.collectionId
      };
    }
    return null;
  } catch (error) {
    console.warn('Error fetching first gallery image for producto:', productoId, error);
    return null;
  }
}

// Fetch producto attributes
export async function fetchAtributosProducto(productoId) {
  try {
    const response = await pb.collection('atributo_producto').getList(1, 50, {
      filter: `producto="${productoId}"`,
      expand: 'atributo',
      $autoCancel: false
    });
    
    return response.items.map(item => ({
      id: item.id,
      atributo: item.expand?.atributo || item.atributo,
      valor: item.valor
    }));
  } catch (error) {
    console.error('Error fetching producto atributos:', error);
    Swal.fire('Error', 'No se pudieron cargar los atributos del producto', 'error');
    return null;
  }
}

// Save producto attributes
export async function saveAtributosProducto(productoId, atributos) {
  // Elimina los atributos existentes antes de guardar los nuevos
  await deleteAtributosProducto(productoId);
  
  try {
    // Itera sobre cada atributo y guarda/actualiza en la colección 'atributo_producto'
    for (const { atributo, valor } of atributos) {
      await pb.collection('atributo_producto').create({
        producto: productoId,
        atributo: atributo.id,
        valor,
      });
    }

    toasts.add({
      title: 'Éxito',
      description: 'Atributos del producto actualizados exitosamente',
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true,
    });
  } catch (error) {
    console.error('Error guardando atributos del producto:', error);
    toasts.add({
      title: 'Error',
      description: 'No se pudieron guardar los atributos del producto',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
  }
}

// Delete producto attributes
export async function deleteAtributosProducto(productoId) {
  try {
    const atributos = await pb.collection('atributo_producto').getFullList({
      filter: `producto = "${productoId}"`
    });
    
    for (const atributo of atributos) {
      await pb.collection('atributo_producto').delete(atributo.id);
    }
  } catch (error) {
    console.error('Error eliminando atributos del producto:', error);
  }
}

// Upload gallery images
export async function uploadGalleryImages(productoId, files) {
  try {
    const uploadPromises = files.map(async (file) => {
      const formData = new FormData();
      formData.append('producto', productoId);
      formData.append('url', file);
      
      return await pb.collection('producto_gallery').create(formData);
    });

    const results = await Promise.all(uploadPromises);
    
    toasts.add({
      title: 'Éxito',
      description: `${results.length} imágenes subidas exitosamente`,
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true,
    });

    return results;
  } catch (error) {
    console.error('Error uploading gallery images:', error);
    toasts.add({
      title: 'Error',
      description: 'Error al subir las imágenes',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
    throw error;
  }
}

// Get available attributes for products
export async function fetchAtributosDisponibles() {
  try {
    const response = await pb.collection('atributo').getFullList({
      filter: `tipo = "producto"`,
      sort: 'descrip'
    });
    return response;
  } catch (error) {
    console.error("Error fetching available atributos:", error);
    return [];
  }
}

// Fetch producto categories
export async function fetchCategoriasProducto(productoId) {
  try {
    const response = await pb.collection('categoria_producto').getList(1, 50, {
      filter: `producto="${productoId}"`,
      expand: 'categoria',
      $autoCancel: false
    });
    
    return response.items.map(item => ({
      id: item.id,
      categoria: item.expand?.categoria || item.categoria
    }));
  } catch (error) {
    console.error('Error fetching categorias producto:', error);
    return [];
  }
}

// Save producto categories
export async function saveCategoriasProducto(productoId, categorias) {
  // Elimina las categorías existentes antes de guardar las nuevas
  await deleteCategoriasProducto(productoId);
  
  try {
    // Itera sobre cada categoría y guarda en la colección 'categoria_producto'
    for (const categoria of categorias) {
      // Handle both multiselect format {key, value} and direct ID format
      const categoriaId = categoria.key || categoria.id || categoria;
      await pb.collection('categoria_producto').create({
        producto: productoId,
        categoria: categoriaId,
      });
    }

    toasts.add({
      title: 'Éxito',
      description: 'Categorías del producto actualizadas exitosamente',
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true,
    });
  } catch (error) {
    console.error('Error guardando categorías del producto:', error);
    toasts.add({
      title: 'Error',
      description: 'No se pudieron guardar las categorías del producto',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
  }
}

// Delete producto categories
export async function deleteCategoriasProducto(productoId) {
  try {
    const categorias = await pb.collection('categoria_producto').getFullList({
      filter: `producto = "${productoId}"`
    });
    
    for (const categoria of categorias) {
      await pb.collection('categoria_producto').delete(categoria.id);
    }
  } catch (error) {
    console.error('Error eliminando categorías del producto:', error);
  }
}

// Get available categories for dropdown
export async function fetchCategoriasDisponibles() {
  try {
    const response = await pb.collection('categoria').getFullList({
      sort: 'descrip',
      expand: 'parent'
    });
    return response;
  } catch (error) {
    console.error("Error fetching available categorias:", error);
    return [];
  }
}
